/**
 * Selection orchestration for the Vue compatibility release.
 *
 * Keeps the rules the React client already follows so both frontends behave identically:
 * - one owned draft per flow; the server owns selection once a checkout exists
 * - a selection write carries the revision the client last saw
 * - a conflict is resolved by re-reading the real state, never by retrying blindly
 * - changing trip cancels the owned hold first so no seat is left locked
 */

export class SelectionError extends Error {
  constructor(code, message, { conflict = false, dropped = [], view = null } = {}) {
    super(message)
    this.name = 'SelectionError'
    this.code = code
    this.conflict = conflict
    /** Seats the server no longer offers, so the UI can explain what was removed. */
    this.dropped = dropped
    /** The authoritative checkout the caller should render after a conflict. */
    this.view = view
  }
}

const CONFLICT_CODES = ['seat_conflict', 'stock_conflict', 'version_conflict']

export function createSelectionFlow(api) {
  let checkout = null

  function requireOrder() {
    if (!checkout) throw new SelectionError('no_checkout', 'Chưa tạo được đơn giữ chỗ.')
    return checkout
  }

  return {
    get checkout() { return checkout },
    get checkoutId() { return checkout ? checkout.checkout_id : null },

    /** Adopts a revision the server reported elsewhere (for example after a conflict read). */
    adoptRevision(revision) {
      if (checkout && Number.isInteger(revision)) checkout = { checkout_id: checkout.checkout_id, revision }
      return checkout
    },

    /** Creates the draft once; later calls reuse it and refresh the revision. */
    async ensureDraft(tripId, ticketTypeId) {
      if (checkout) return checkout
      await api.bootstrapGuest()
      const created = await api.createDraft({ trip_id: Number(tripId), ticket_type_id: Number(ticketTypeId) },
        `draft-${tripId}-${ticketTypeId}-${Date.now().toString(36)}`)
      checkout = { checkout_id: created.checkout_id, revision: created.revision }
      return checkout
    },

    async loadAvailability(tripId) {
      await api.bootstrapGuest()
      const view = await api.availability(tripId)
      if (!view || Number(view.trip_id) !== Number(tripId)) {
        throw new SelectionError('invalid_response', 'Sơ đồ ghế không khớp chuyến.')
      }
      return view.seats || []
    },

    /**
     * Reserves the chosen seats. Returns { view, dropped } where `dropped` lists seats the
     * server no longer offers, so the UI can explain why a seat disappeared.
     */
    async reserveSeats(seatIds, { quantity, addons = [] } = {}) {
      const order = requireOrder()
      const wanted = [...new Set(seatIds)]
      if (!Array.isArray(addons) || addons.some((line) => !line || typeof line.addon_id !== 'number'
        || !Number.isInteger(line.quantity) || line.quantity < 1)) {
        throw new SelectionError('invalid_request', 'Danh sách dịch vụ không hợp lệ.')
      }
      if (wanted.some((seat) => typeof seat !== 'string' || seat.length === 0 || seat.length > 20)) {
        throw new SelectionError('invalid_request', 'Ghế không hợp lệ.')
      }
      const count = Number.isInteger(quantity) && quantity > 0 ? quantity : Math.max(1, wanted.length)
      try {
        const view = await api.replaceSelection(order.checkout_id, {
          expected_revision: order.revision, seat_ids: wanted, quantity: count, addons,
        }, `selection-${order.checkout_id}-${order.revision}`)
        checkout = { checkout_id: view.checkout_id, revision: view.revision }
        return { view, dropped: [] }
      } catch (error) {
        if (!error || !CONFLICT_CODES.includes(error.code)) throw error
        // Re-read the truth: the server owns what is still selectable.
        const current = await api.readCheckout(order.checkout_id)
        checkout = { checkout_id: current.checkout_id, revision: current.revision }
        const stillMine = new Set((current.selection && current.selection.seat_ids) || [])
        const dropped = wanted.filter((seat) => !stillMine.has(seat))
        throw new SelectionError(error.code, error.message, { conflict: true, dropped, view: current })
      }
    },

    /** Releases the owned hold before leaving the flow or switching trip. */
    async release(reason = 'changed') {
      if (!checkout) return null
      const order = checkout
      try {
        const view = await api.cancel(order.checkout_id, { expected_revision: order.revision },
          `cancel-${order.checkout_id}-${order.revision}`)
        return view
      } finally {
        // The local flow never keeps a released checkout: a stale id would let the UI
        // claim a hold the server no longer has.
        checkout = null
        if (reason === 'changed') api.reset()
      }
    },
  }
}
